// src/pages/admin/members/AdminInnerCircle.tsx
//
// Focused view of the Inner Circle tier — the mentees who have progressed
// beyond Wider Audience and are being groomed for Legacy Fellows consideration.
//
// ACCESS:
//   admin / committee_chair  — full view, edit tier, advance quarter, nominate
//   portal_manager           — read-only list; can advance quarter
//   committee_member         — read-only
//   regional_rep             — no access (tab hidden)
//
// ACTIONS CAPTURED:
//   GET   /admin/users?tier=inner_circle  — filtered member list
//   PATCH /admin/users/:id               — region, province, ministryRole edits
//   PATCH /admin/mentees/:id/set-tier    — upgrade to legacy_fellows or downgrade
//   PATCH /admin/mentees/:id/advance-quarter — move to next quarter
//   POST  /fellows/nominate              — nominate for Legacy Fellows

import React, { useState, useMemo, useEffect } from "react";
import {
  Search,
  Users,
  Star,
  TrendingUp,
  ChevronLeft,
  ChevronRight,
  RefreshCw,
  BarChart2,
  Pencil,
} from "lucide-react";

import {
  CAN_VIEW_IC,
  CAN_EDIT_USERS,
  CAN_NOMINATE,
  QUARTERS,
  PAGE_SIZE,
  Bone,
  FL,
  Textarea,
  Sel,
  Avatar,
  EditMemberPanel,
  StatTile,
} from "./MemberShared";
import { ManagedUser, useGetUsersQuery } from "../../app/api/adminApi";
import { QuarterKey } from "../../app/api/programmeApi";
import { useAppSelector } from "../../app/hooks";
import { UserRole } from "../../app/api/authApi";
import { useNominateFellowMutation } from "../../app/api/fellowsApi";

/* ─────────────────────────────────────────────────────────────────
   IC MEMBER ROW
───────────────────────────────────────────────────────────────── */
const ICRow: React.FC<{
  member: ManagedUser;
  canEdit: boolean;
  canNominate: boolean;
  onEdit: (m: ManagedUser) => void;
  onNominate: (m: ManagedUser) => void;
}> = ({ member, canEdit, canNominate, onEdit, onNominate }) => {
  const profile = member.profile as any;
  const quarter = profile?.currentQuarter ?? "Q1";
  const avg = profile?.overallAvgScore != null ? profile.overallAvgScore : null;
  const submitted = profile?.totalTasksSubmitted ?? 0;

  const scoreColour =
    avg === null
      ? "text-gray-400"
      : avg >= 8
        ? "text-green-600"
        : avg >= 5
          ? "text-amber-600"
          : "text-red-500";

  return (
    <div
      className={`flex items-center gap-4 px-5 py-3.5 hover:bg-gray-50 transition-colors ${!member.isActive ? "opacity-50" : ""}`}
    >
      <Avatar user={member} size="sm" colour="bg-blue-100 text-blue-700" />

      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-0.5 flex-wrap">
          <p className="text-sm font-semibold text-gray-900 truncate">
            {member.firstName} {member.lastName}
          </p>
          {member.fellowsEligible && (
            <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-600 shrink-0 flex items-center gap-0.5">
              <Star size={8} />
              Fellows eligible
            </span>
          )}
          {!member.isActive && (
            <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-red-100 text-red-600 shrink-0">
              Inactive
            </span>
          )}
        </div>
        <p className="text-[11px] text-gray-400 truncate">{member.email}</p>
      </div>

      {/* Region */}
      <div className="hidden md:block shrink-0 w-28 text-right">
        <p className="text-[11px] text-gray-500 truncate">
          {member.region?.split("—")[1]?.trim() ?? member.region ?? "—"}
        </p>
      </div>

      {/* Current quarter */}
      <div className="hidden sm:block shrink-0">
        <span className="text-[11px] font-semibold text-purple-700 bg-purple-50 px-2 py-0.5 rounded">
          {quarter}
        </span>
      </div>

      {/* Tasks submitted */}
      <div className="hidden lg:block shrink-0 text-right w-14">
        <p className="text-xs font-semibold text-gray-700">{submitted}</p>
        <p className="text-[10px] text-gray-400">tasks</p>
      </div>

      {/* Avg score ring */}
      <div className="hidden sm:block shrink-0 text-right w-12">
        {avg !== null ? (
          <>
            <p className={`text-sm font-bold ${scoreColour}`}>{avg}</p>
            <p className="text-[10px] text-gray-400">/10 avg</p>
          </>
        ) : (
          <p className="text-[11px] text-gray-400">—</p>
        )}
      </div>

      {/* Actions */}
      <div className="shrink-0 flex items-center gap-1">
        {canNominate && member.isActive && (
          <button
            onClick={() => onNominate(member)}
            title="Nominate for Legacy Fellows"
            className="p-1.5 rounded-lg text-amber-500 hover:bg-amber-50 transition-colors"
          >
            <Star size={14} />
          </button>
        )}
        {canEdit && (
          <button
            onClick={() => onEdit(member)}
            title="Edit"
            className="p-1.5 rounded-lg text-gray-400 hover:text-purple-600 hover:bg-purple-50 transition-colors"
          >
            <Pencil size={14} />
          </button>
        )}
      </div>
    </div>
  );
};

/* ─────────────────────────────────────────────────────────────────
   NOMINATE MODAL  (inline — same as AllMembers)
───────────────────────────────────────────────────────────────── */
const NominateModal: React.FC<{
  mentee: ManagedUser;
  isLoading: boolean;
  apiError: string | null;
  onConfirm: (quarter: QuarterKey, comment: string) => void;
  onCancel: () => void;
}> = ({ mentee, isLoading, apiError, onConfirm, onCancel }) => {
  const [quarter, setQuarter] = useState<QuarterKey>("Q1");
  const [comment, setComment] = useState("");

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40">
      <div className="bg-white rounded-2xl shadow-xl max-w-sm w-full p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center shrink-0">
            <Star size={18} className="text-amber-600" />
          </div>
          <div>
            <h3 className="text-sm font-bold text-gray-900">
              Nominate for Legacy Fellows
            </h3>
            <p className="text-xs text-gray-500">
              {mentee.firstName} {mentee.lastName}
            </p>
          </div>
        </div>
        <div className="space-y-3 mb-4">
          <div>
            <FL required>Quarter</FL>
            <Sel
              value={quarter}
              onChange={(e) => setQuarter(e.target.value as QuarterKey)}
            >
              {QUARTERS.map((q) => (
                <option key={q} value={q}>
                  {q}
                </option>
              ))}
            </Sel>
          </div>
          <div>
            <FL>
              Note <span className="font-normal text-gray-400">(optional)</span>
            </FL>
            <Textarea
              rows={3}
              placeholder="Why nominate this member?"
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              maxLength={500}
            />
          </div>
        </div>
        {apiError && (
          <p className="text-xs text-red-600 bg-red-50 rounded-lg px-3 py-2 mb-3">
            {apiError}
          </p>
        )}
        <div className="flex gap-3">
          <button
            onClick={onCancel}
            className="flex-1 py-2 text-sm font-medium border border-gray-200 rounded-lg hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            onClick={() => onConfirm(quarter, comment)}
            disabled={isLoading}
            className="flex-1 py-2 text-sm font-semibold bg-amber-500 text-white rounded-lg hover:bg-amber-600 disabled:opacity-50"
          >
            {isLoading ? "Nominating…" : "Nominate"}
          </button>
        </div>
      </div>
    </div>
  );
};

/* ─────────────────────────────────────────────────────────────────
   PAGE
───────────────────────────────────────────────────────────────── */
const AdminInnerCircle: React.FC = () => {
  const user = useAppSelector((s) => s.auth.user)!;
  const role = (user.role ?? "committee_member") as UserRole;

  const canView = CAN_VIEW_IC.includes(role) || role === "portal_manager";
  const canEdit = CAN_EDIT_USERS.includes(role);
  const canNominate = CAN_NOMINATE.includes(role);

  const [search, setSearch] = useState("");
  const [quarterF, setQuarterF] = useState<QuarterKey | "all">("all");
  const [page, setPage] = useState(1);

  const [editTarget, setEditTarget] = useState<ManagedUser | null>(null);
  const [nominateTarget, setNominateTarget] = useState<ManagedUser | null>(
    null,
  );
  const [nominateErr, setNominateErr] = useState<string | null>(null);

  const queryArgs = useMemo(
    () => ({
      tier: "inner_circle" as const,
      ...(search.trim() ? { search: search.trim() } : {}),
      limit: PAGE_SIZE,
      page,
    }),
    [search, page],
  );

  const {
    data: usersRes,
    isLoading,
    isFetching,
  } = useGetUsersQuery(queryArgs, { skip: !canView });
  const [nominateFellow, { isLoading: isNominating }] =
    useNominateFellowMutation();

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const members = usersRes?.data ?? [];
  const totalCount = usersRes?.total ?? 0;
  const totalPages = usersRes?.pages ?? 1;

  useEffect(() => {
    setPage(1);
  }, [search]);

  // Client-side quarter filter (profile.currentQuarter isn't a server filter param)
  const filtered = useMemo(() => {
    if (quarterF === "all") return members;
    return members.filter(
      (m) => (m.profile as any)?.currentQuarter === quarterF,
    );
  }, [members, quarterF]);

  // IC aggregate stats across the current page
  const stats = useMemo(() => {
    const scores = members
      .map((m) => (m.profile as any)?.overallAvgScore)
      .filter((s) => s != null) as number[];
    return {
      total: totalCount,
      avgScore:
        scores.length > 0
          ? (scores.reduce((a, b) => a + b, 0) / scores.length).toFixed(1)
          : "—",
      eligible: members.filter((m) => m.fellowsEligible).length,
      onQ4: members.filter((m) => (m.profile as any)?.currentQuarter === "Q4")
        .length,
    };
  }, [members, totalCount]);

  const handleNominate = async (quarter: QuarterKey, comment: string) => {
    if (!nominateTarget) return;
    setNominateErr(null);
    try {
      await nominateFellow({
        menteeId: nominateTarget.id,
        quarter,
        nominationComment: comment || undefined,
      }).unwrap();
      setNominateTarget(null);
    } catch (err: any) {
      setNominateErr(err?.data?.error ?? "Could not nominate.");
    }
  };

  /* Access gate */
  if (!canView) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-center gap-3">
        <Users size={28} className="text-gray-300" />
        <p className="text-sm font-semibold text-gray-600">Access restricted</p>
        <p className="text-xs text-gray-400 max-w-xs">
          Inner Circle management requires Admin or Committee Chair access.
        </p>
      </div>
    );
  }

  /* ──────────────────────────────────────────────────────────────── */
  return (
    <div className="space-y-5 pb-8">
      {/* Summary tiles */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <StatTile
          label="Inner Circle total"
          value={stats.total}
          colour="text-blue-700"
          icon={<Users size={12} className="text-blue-400" />}
          loading={isLoading}
        />
        <StatTile
          label="Fellows eligible"
          value={stats.eligible}
          colour="text-amber-600"
          icon={<Star size={12} className="text-amber-400" />}
        />
        <StatTile
          label="Avg. score"
          value={stats.avgScore}
          colour="text-green-700"
          icon={<BarChart2 size={12} className="text-green-400" />}
        />
        <StatTile
          label="On Q4"
          value={stats.onQ4}
          colour="text-purple-700"
          icon={<TrendingUp size={12} className="text-purple-400" />}
        />
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search
            size={14}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
          />
          <input
            type="text"
            placeholder="Search name or email…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-9 pr-4 py-2.5 text-sm rounded-xl border border-gray-200 bg-white focus:outline-none focus:ring-2 focus:ring-purple-300 placeholder-gray-400 transition"
          />
        </div>
        <div className="flex items-center gap-1 bg-gray-100 rounded-xl p-1 shrink-0">
          {(["all", ...QUARTERS] as const).map((q) => (
            <button
              key={q}
              onClick={() => setQuarterF(q as QuarterKey | "all")}
              className={`text-xs font-semibold px-3 py-1.5 rounded-lg transition-all
                ${quarterF === q ? "bg-white text-gray-900 shadow-sm" : "text-gray-500 hover:text-gray-700"}`}
            >
              {q === "all" ? "All" : q}
            </button>
          ))}
        </div>
      </div>

      {/* Member list */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
        <div className="px-5 py-3 border-b border-gray-100 flex items-center justify-between">
          <p className="text-xs font-bold text-gray-700">
            {isLoading
              ? "Loading…"
              : `${filtered.length} of ${totalCount.toLocaleString()} member${totalCount !== 1 ? "s" : ""}`}
          </p>
          {isFetching && !isLoading && (
            <RefreshCw size={12} className="text-gray-400 animate-spin" />
          )}
        </div>

        {/* Column labels */}
        <div className="hidden lg:grid grid-cols-[1fr_120px_60px_50px_48px_72px] gap-4 px-5 py-2 border-b border-gray-50">
          {["Member", "Region", "Quarter", "Tasks", "Avg", "Actions"].map(
            (h) => (
              <p
                key={h}
                className="text-[10px] font-bold uppercase tracking-wide text-gray-400"
              >
                {h}
              </p>
            ),
          )}
        </div>

        {isLoading ? (
          <div className="p-4 space-y-3">
            {[0, 1, 2, 3, 4].map((i) => (
              <Bone key={i} className="h-14" />
            ))}
          </div>
        ) : filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-center gap-2">
            <Users size={24} className="text-gray-300" />
            <p className="text-sm font-semibold text-gray-600">
              No Inner Circle members found
            </p>
            <p className="text-xs text-gray-400">
              Try a different search or quarter filter.
            </p>
          </div>
        ) : (
          <div className="divide-y divide-gray-50">
            {filtered.map((m) => (
              <ICRow
                key={m.id}
                member={m}
                canEdit={canEdit}
                canNominate={canNominate}
                onEdit={setEditTarget}
                onNominate={setNominateTarget}
              />
            ))}
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="px-5 py-3 border-t border-gray-100 flex items-center justify-between">
            <p className="text-[11px] text-gray-400">
              Page {page} of {totalPages}
            </p>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
                className="p-1.5 rounded-lg border border-gray-200 disabled:opacity-40 hover:bg-gray-50"
              >
                <ChevronLeft size={14} />
              </button>
              <button
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="p-1.5 rounded-lg border border-gray-200 disabled:opacity-40 hover:bg-gray-50"
              >
                <ChevronRight size={14} />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Panels & modals */}
      {editTarget && (
        <EditMemberPanel
          member={editTarget}
          callerRole={role}
          onClose={() => setEditTarget(null)}
        />
      )}

      {nominateTarget && (
        <NominateModal
          mentee={nominateTarget}
          isLoading={isNominating}
          apiError={nominateErr}
          onConfirm={handleNominate}
          onCancel={() => {
            setNominateTarget(null);
            setNominateErr(null);
          }}
        />
      )}
    </div>
  );
};

export default AdminInnerCircle;
